// BillingAddressForm.tsx
import React, { useEffect, useState } from 'react';
import { Grid, MenuItem } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { TextField } from 'formik-mui';
import { Country, IState, State } from 'country-state-city';
import { dropdownKeyEventHandler } from 'renderer/shared/callbacks/keyEventHandler';
import { AddressForm } from '../schemas/paymentMethods.schemas';

interface BillingAddressFormProps {
  disabled: boolean;
}

// Sort countries alphabetically, ensuring USA is first
const countries = Country.getAllCountries().sort((a, b) => {
  if (a.isoCode === 'US') return -1;
  if (b.isoCode === 'US') return 1;
  return a.name.localeCompare(b.name);
});

const BillingAddressForm: React.FC<BillingAddressFormProps> = ({
  disabled,
}) => {
  const { values, setFieldValue } = useFormikContext<AddressForm>();
  const [regions, setRegions] = useState<IState[]>([]);
  // const [countryCode, setCountryCode] = useState<string>('');

  useEffect(() => {
    if (values.countryCode) {
      setRegions(State.getStatesOfCountry(values.countryCode));
      setFieldValue(
        'countryName',
        Country.getCountryByCode(values.countryCode)?.name ?? '',
      );
      // setFieldValue('regionCode', '', false);
      // setFieldValue('region', '', false);
    }
  }, [setFieldValue, values.countryCode]);

  useEffect(() => {
    setFieldValue(
      'region',
      State.getStateByCodeAndCountry(values.regionCode, values.countryCode)
        ?.name ?? '',
    );
  }, [setFieldValue, values.countryCode, values.regionCode]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Field
          disabled={disabled}
          component={TextField}
          fullWidth
          size="small"
          label="Name on Card"
          name="nameOnCard"
        />
      </Grid>
      <Grid item xs={12}>
        <Field
          component={TextField}
          select
          onKeyUp={dropdownKeyEventHandler(
            countries.map(({ isoCode, name }) => ({
              label: name,
              value: isoCode,
            })),
            (option) => setFieldValue('countryCode', option),
          )}
          fullWidth
          size="small"
          disabled={disabled}
          name="countryCode"
          labelId="countryCode"
          label="Country"
        >
          <MenuItem value="" disabled sx={{ color: 'grey' }}>
            <em>Country</em>
          </MenuItem>
          {countries.map((c) => (
            <MenuItem key={c.isoCode} value={c.isoCode}>
              {c.name}
            </MenuItem>
          ))}
        </Field>
      </Grid>
      <Grid item xs={12}>
        <Field
          component={TextField}
          disabled={disabled}
          fullWidth
          size="small"
          label="Address"
          name="streetAddress"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Field
          component={TextField}
          disabled={disabled}
          fullWidth
          size="small"
          label="City"
          name="locality"
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Field
          component={TextField}
          onKeyUp={dropdownKeyEventHandler(
            regions.map(({ isoCode, name }) => ({
              label: name,
              value: isoCode,
            })),
            (option) => setFieldValue('regionCode', option),
          )}
          disabled={disabled}
          fullWidth
          select
          size="small"
          id="regionCode"
          name="regionCode"
          labelId="regionCode"
          label="Region"
        >
          <MenuItem value="" disabled sx={{ color: 'grey' }}>
            <em>Region</em>
          </MenuItem>
          {regions.map((r) => (
            <MenuItem key={r.isoCode} value={r.isoCode}>
              {r.name}
            </MenuItem>
          ))}
        </Field>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Field
          component={TextField}
          disabled={disabled}
          fullWidth
          size="small"
          label="Postal Code"
          name="postalCode"
        />
      </Grid>
    </Grid>
  );
};

export default BillingAddressForm;
